/**
 * API Client Configuration Store
 *
 * Stores configuration (URLs, callbacks) used by edge-function callers,
 * the data-proxy client, and analytics hooks. No Supabase JS dependency.
 *
 * Auth is handled entirely by Better Auth.
 */
import type { ApiClientConfig } from './types';

let clientConfig: ApiClientConfig | null = null;

/**
 * Initialise the API client configuration.
 *
 * Must be called once at app startup (in _layout.tsx) before any API call.
 * Validates that required fields are present.
 */
export function initClientConfig(config: ApiClientConfig): void {
  if (!config.url) {
    throw new Error(
      'API URL is required. Set EXPO_PUBLIC_CF_WORKER_URL in your environment.',
    );
  }

  // Guard against the workers.dev URL pitfall (prod and staging).
  // On 2026-04-15 the prod dd-workers.liftping.workers.dev subdomain stopped
  // routing while the api.dramadetector.com custom domain stayed up, taking
  // every authenticated web request down with a "TypeError: Failed to fetch".
  // Both prod and staging now have custom domains: api.dramadetector.com and
  // api-staging.dramadetector.com (the staging custom domain was added on
  // 2026-04-15 per NocoDB #3639). The bare workers.dev subdomains are still
  // resolvable but should not be used by clients — they can be disabled at
  // any time. See docs/supabase-decommission/external-wiring-audit.md.
  const workerUrl = config.cfWorkerBaseUrl || config.url;
  if (workerUrl && /\bdd-workers(-staging)?\.liftping\.workers\.dev\b/i.test(workerUrl)) {
    console.warn(
      '[api-client] EXPO_PUBLIC_CF_WORKER_URL points at a workers.dev ' +
      `URL (${workerUrl}). Use the custom domain instead ` +
      '(api.dramadetector.com for prod, api-staging.dramadetector.com for ' +
      'staging) — the workers.dev subdomain can be disabled and on 2026-04-15 ' +
      'the prod one took the app down with "Failed to fetch" until the env ' +
      'var was repointed at the custom domain.',
    );
  }

  clientConfig = config;
}

/**
 * Get the stored client config.
 * @internal Used by API functions, edge-url builders, and anonymous trial support.
 */
export function getClientConfig(): ApiClientConfig | null {
  return clientConfig;
}

/**
 * Reset the client config (useful for testing).
 */
export function resetClientConfig(): void {
  clientConfig = null;
}
